import { useEffect, useState } from "react";
import {
  collection, doc, onSnapshot, query, where, serverTimestamp, writeBatch, updateDoc, deleteDoc, deleteField, getDocs, setDoc,
} from "firebase/firestore";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import { syncBuildingCount, openBillingPortal } from "../lib/billing";
import { S, C } from "../ui";
import { TRADES, TIME_ZONES } from "../constants";
import AssetsPanel from "./AssetsPanel";
import TemplatesPanel from "./TemplatesPanel";
import WorkersPanel from "./WorkersPanel";
import ScorecardPanel from "./ScorecardPanel";
import IncidentsPanel from "./IncidentsPanel";
import ReportPanel from "./ReportPanel";
import PortfolioPanel from "./PortfolioPanel";
import AlertsPanel from "./AlertsPanel";
import MessagesPanel from "./MessagesPanel";
import { FacilityWorkOrdersTab } from "./WorkOrdersPanel";
import EntityDetailPanel from "./EntityDetailPanel";
import OnboardingChecklist from "./OnboardingChecklist";
import ScrollHint from "../core/ScrollHint";
import lockupLight from "../assets/lockup-light.svg";

function genInviteCode() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // no 0/O/1/I — easier to read aloud
  let s = "";
  for (let i = 0; i < 8; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return s;
}

export default function FacilityAdminDashboard({ membership }) {
  const [activeFacilityId, setActiveFacilityId] = useState(membership.facilityId);
  const facilityId = activeFacilityId;
  const [allFacilities, setAllFacilities] = useState([]);
  const [restoreBusy, setRestoreBusy] = useState(null); // facilityId currently restoring, or null
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [addingBuilding, setAddingBuilding] = useState(false);
  const [newBuildingName, setNewBuildingName] = useState("");
  const [newBuildingAddress, setNewBuildingAddress] = useState("");
  const [newBuildingTimeZone, setNewBuildingTimeZone] = useState("America/Chicago");
  const [buildingErr, setBuildingErr] = useState("");
  const [removingBuildingId, setRemovingBuildingId] = useState(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [copiedInvites, setCopiedInvites] = useState(null); // [{ vendorName, code }] from the last "+ Add Building"
  const [staffAccessCode, setStaffAccessCode] = useState(undefined); // undefined = loading, null = none generated yet
  const [generatingCode, setGeneratingCode] = useState(false);
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  const [tab, setTab] = useState("vendors");
  const [facility, setFacility] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [tradeFilter, setTradeFilter] = useState("all");
  const [adding, setAdding] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState("");
  const [newTrade, setNewTrade] = useState(TRADES[0]);
  const [lastInvite, setLastInvite] = useState(null); // { code, vendorName }
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [selectedVendorWorker, setSelectedVendorWorker] = useState(null); // crew member within the selected vendor, or null
  const [selectedStaff, setSelectedStaff] = useState(null); // full worker doc, not just id
  const [err, setErr] = useState("");

  // Portfolio support (spec open decision #2, resolved 2026-09-07): a
  // Facility Admin can own more than one building. Each building is its
  // own facilities/{id} doc sharing this admin's ownerUid — every other
  // collection (vendors, assets, workers, checklists...) is already
  // scoped by facilityId, so switching buildings is just switching which
  // facilityId every child panel below queries by. See firestore.rules'
  // isFacilityAdmin() for the ownership-based rule this depends on.
  useEffect(() => onSnapshot(query(collection(db, "facilities"), where("ownerUid", "==", auth.currentUser.uid)), s =>
    setAllFacilities(s.docs.map(d => ({ id: d.id, ...d.data() })))), []);
  // active:false (removed, see removeBuilding below) split out in code,
  // not in the query itself — a `!=` filter would also silently exclude
  // every building created before this field existed.
  const myFacilities = allFacilities.filter(f => f.active !== false);
  const removedFacilities = allFacilities.filter(f => f.active === false);

  // Carries over the *currently viewed* building's vendor roster,
  // internal staff, and asset/room list into the new one — a portfolio
  // owner's cleaning/HVAC/etc. vendors (and often room layout — many
  // buildings in one portfolio share a similar floor plan) are usually
  // close to identical across several of their buildings, so starting
  // from scratch every time would mean redoing the whole invite-code
  // dance and re-typing every room name for no reason. Copies are fully
  // independent rows from the moment they're created (own vendor doc,
  // own contract, own invite code, own asset doc/QR id) — freely
  // editable/removable per building afterward, not a live link back to
  // the source.
  const addBuilding = async () => {
    if (!newBuildingName.trim()) { setBuildingErr("Enter a building name."); return; }
    setBuildingErr("");
    const ref = doc(collection(db, "facilities"));

    try {
      // Committed alone, first: every copied vendor/staff write below is
      // authorized by isFacilityAdmin(ref.id), which checks ownerUid on
      // *this exact facilities doc* — inside one shared batch with those
      // writes, the doc wouldn't exist yet at rule-evaluation time (a
      // batch's writes are each checked against the currently-committed
      // database, not against each other), so every one of them would be
      // denied. Awaiting this commit first makes the doc real before
      // anything that depends on it runs.
      await writeBatch(db).set(ref, {
        name: newBuildingName.trim(),
        address: newBuildingAddress.trim(),
        timeZone: newBuildingTimeZone,
        ownerUid: auth.currentUser.uid,
        isPrimary: false,
        createdAt: serverTimestamp(),
      }).commit();
    } catch (e) {
      setBuildingErr("Couldn't add building — try again.");
      return;
    }

    setActiveFacilityId(ref.id);
    setNewBuildingName(""); setNewBuildingAddress(""); setAddingBuilding(false); setSwitcherOpen(false);
    syncBuildingCount(auth.currentUser.uid); // true up Stripe — fire-and-forget, never blocks the UI

    // Best-effort from here — the building itself already exists and is
    // usable even if copying its starting roster fails for some reason,
    // so a failure here doesn't roll back or error-message the building
    // creation the admin already saw succeed.
    try {
      const rosterBatch = writeBatch(db);
      const today = new Date().toISOString().slice(0, 10);
      const newInvites = [];
      for (const v of vendors) {
        const vendorRef = doc(collection(db, "vendors"));
        const contractRef = doc(collection(db, "vendorContracts"));
        const code = genInviteCode();
        rosterBatch.set(vendorRef, { facilityId: ref.id, companyName: v.companyName, trade: v.trade, createdAt: serverTimestamp() });
        rosterBatch.set(contractRef, {
          facilityId: ref.id, vendorId: vendorRef.id,
          vendorName: v.companyName, trade: v.trade,
          startDate: today, endDate: null, createdAt: serverTimestamp(),
        });
        rosterBatch.set(doc(db, "inviteCodes", code), { facilityId: ref.id, vendorId: vendorRef.id, used: false, createdAt: serverTimestamp() });
        newInvites.push({ vendorName: v.companyName, code });
      }

      // Internal staff have no membership/login concept (they clock in
      // via QR scan + their own PIN, not a real account — see
      // FieldFlow.jsx), so copying them is complete on its own: the same
      // PIN works immediately at the new building too, no re-invitation
      // needed.
      const staffSnap = await getDocs(query(
        collection(db, "workers"), where("facilityId", "==", facilityId), where("roleType", "==", "internal_staff")
      ));
      for (const s of staffSnap.docs) {
        const w = s.data();
        rosterBatch.set(doc(collection(db, "workers")), {
          facilityId: ref.id, vendorId: null, roleType: "internal_staff",
          trade: w.trade, name: w.name, pin: w.pin, createdAt: serverTimestamp(),
        });
      }

      // Each copy gets its own new asset doc/id — and therefore its own
      // QR code — since it's a different physical room/unit in a
      // different building even when the name matches (e.g. "Lobby" at
      // Building A and Building B are two different QR codes to print).
      const assetsSnap = await getDocs(query(collection(db, "assets"), where("facilityId", "==", facilityId)));
      for (const a of assetsSnap.docs) {
        const asset = a.data();
        rosterBatch.set(doc(collection(db, "assets")), {
          facilityId: ref.id, name: asset.name, trade: asset.trade, assetType: asset.assetType, createdAt: serverTimestamp(),
        });
      }

      await rosterBatch.commit();
      setCopiedInvites(newInvites.length ? newInvites : null);
    } catch (e) {
      console.warn("Copying the starting vendor/staff roster onto the new building failed (non-fatal):", e);
    }
  };

  // Soft-delete, not a hard delete — sets active:false rather than
  // deleting the facilities doc (or, worse, only the doc and none of its
  // vendors/checklists/reports, which firestore.rules' ownership-based
  // isFacilityAdmin() would then make permanently unreachable to clean up,
  // since it needs the facilities doc to still exist to authorize
  // anything scoped to that facilityId). Keeping every vendor/checklist/
  // report intact also matches this whole product's own stated promise —
  // "swapping/removing a vendor never orphans history" — applied one
  // level up to buildings themselves. The $100/mo charge still ends
  // immediately via reconcileBuildingCount, which already only counts
  // active buildings.
  const removeBuilding = async (target) => {
    setRemoveBusy(true);
    try {
      await updateDoc(doc(db, "facilities", target.id), { active: false, removedAt: serverTimestamp() });
      if (activeFacilityId === target.id) {
        const fallback = myFacilities.find(f => f.id !== target.id && f.isPrimary !== false) || myFacilities.find(f => f.id !== target.id);
        if (fallback) setActiveFacilityId(fallback.id);
      }
      setRemovingBuildingId(null);
      syncBuildingCount(auth.currentUser.uid); // true up Stripe — fire-and-forget, never blocks the UI
    } catch (e) {
      // No dedicated error UI here — the confirm panel just stays open
      // with the button re-enabled, matching the low-stakes/reversible
      // nature of a soft-delete (nothing is lost, so a silent retry
      // opportunity is enough).
    }
    setRemoveBusy(false);
  };

  // Flips active back on. syncBuildingCount is called here deliberately —
  // discovered live that restoring a building purely by flipping the
  // field (e.g. by hand, before this button existed) leaves Stripe's
  // quantity stale until something explicitly re-syncs it, since nothing
  // else triggers on an active:false -> true transition.
  const restoreBuilding = async (target) => {
    setRestoreBusy(target.id);
    try {
      await updateDoc(doc(db, "facilities", target.id), { active: true, removedAt: deleteField() });
      setActiveFacilityId(target.id);
      setSwitcherOpen(false);
      syncBuildingCount(auth.currentUser.uid); // true up Stripe — fire-and-forget, never blocks the UI
    } catch (e) {
      // Silent retry opportunity, same reasoning as removeBuilding above.
    }
    setRestoreBusy(null);
  };

  // Falls back off the Portfolio tab if it stops being available (e.g.
  // the admin removes buildings down to just one while viewing it).
  useEffect(() => { if (tab === "portfolio" && myFacilities.length <= 1) setTab("vendors"); }, [tab, myFacilities.length]);

  // Lets a Portfolio coverage-matrix cell or "needs attention" item jump
  // straight to the relevant building/tab/trade filter — reuses the same
  // setters the building switcher and trade-pill row already call.
  const jumpFromPortfolio = (targetFacilityId, targetTab, trade) => {
    setActiveFacilityId(targetFacilityId);
    if (trade) setTradeFilter(trade);
    setTab(targetTab);
  };

  useEffect(() => onSnapshot(doc(db, "facilities", facilityId), s => setFacility(s.exists() ? s.data() : null)), [facilityId]);
  useEffect(() => onSnapshot(query(collection(db, "vendors"), where("facilityId", "==", facilityId)), s =>
    setVendors(s.docs.map(d => ({ id: d.id, ...d.data() })))), [facilityId]);
  useEffect(() => onSnapshot(query(collection(db, "vendorContracts"), where("facilityId", "==", facilityId)), s =>
    setContracts(s.docs.map(d => ({ id: d.id, ...d.data() })))), [facilityId]);

  // Phase 11: one persistent staff access code per building — see
  // firestore.rules' facilityAccessCodes and StaffAuthScreen.jsx. Doc ID
  // *is* the code (mirrors the vendor inviteCodes shape), so this is a
  // `list`-by-facilityId query rather than a lookup by known ID.
  useEffect(() => onSnapshot(query(collection(db, "facilityAccessCodes"), where("facilityId", "==", facilityId)), s =>
    setStaffAccessCode(s.empty ? null : s.docs[0].id)), [facilityId]);

  const generateStaffAccessCode = async () => {
    setGeneratingCode(true);
    try {
      const code = genInviteCode();
      // Denormalized facilityName so StaffAuthScreen never needs to read
      // facilities/{id} directly — an anonymous staff session (no
      // membership doc) isn't authorized to per firestore.rules'
      // isFacilityAdmin()/vendor-admin-only `get` rule on that collection,
      // and broadening that rule for a display-only name isn't worth
      // widening a core tenant-boundary collection's read surface.
      await setDoc(doc(db, "facilityAccessCodes", code), { facilityId, facilityName: facility.name, createdAt: serverTimestamp() });
    } catch (e) {
      // Silent retry opportunity — the button just re-enables.
    }
    setGeneratingCode(false);
  };

  // Revokes a leaked/no-longer-trusted code and issues a fresh one in its
  // place. Deleting the old doc is what actually revokes it — StaffAuthScreen
  // looks a code up by a direct getDoc on facilityAccessCodes/{code}, so a
  // missing doc means "invalid code" immediately. Staff already signed in
  // (edifice_staff_session cached in their own localStorage) are unaffected;
  // this only blocks new logins with the old code, same as rotating a wifi
  // password.
  const revokeAndRegenerateCode = async () => {
    setGeneratingCode(true);
    try {
      if (staffAccessCode) await deleteDoc(doc(db, "facilityAccessCodes", staffAccessCode));
      const code = genInviteCode();
      await setDoc(doc(db, "facilityAccessCodes", code), { facilityId, facilityName: facility.name, createdAt: serverTimestamp() });
    } catch (e) {
      // Silent retry opportunity — the button just re-enables.
    }
    setGeneratingCode(false);
    setConfirmingRevoke(false);
  };

  const visibleVendors = tradeFilter === "all" ? vendors : vendors.filter(v => v.trade === tradeFilter);
  const contractsFor = (vendorId) => contracts.filter(c => c.vendorId === vendorId).sort((a, b) => (b.startDate || "").localeCompare(a.startDate || ""));
  const selectedVendor = vendors.find(v => v.id === selectedVendorId);

  const addVendor = async () => {
    if (!newCompanyName.trim()) { setErr("Enter a company name."); return; }
    setErr("");
    const vendorRef = doc(collection(db, "vendors"));
    const contractRef = doc(collection(db, "vendorContracts"));
    const code = genInviteCode();
    const batch = writeBatch(db);
    batch.set(vendorRef, {
      facilityId, companyName: newCompanyName.trim(), trade: newTrade, createdAt: serverTimestamp(),
    });
    batch.set(contractRef, {
      facilityId, vendorId: vendorRef.id,
      // vendorName/trade snapshotted here (not just looked up live from
      // vendors/{id}) so Phase 4's compliance reports can still show who
      // was responsible even after a vendor is later removed — "swapping
      // a vendor never orphans history" (spec section 2) only actually
      // holds if the name survives the vendor doc's deletion.
      vendorName: newCompanyName.trim(), trade: newTrade,
      startDate: new Date().toISOString().slice(0, 10), endDate: null,
      createdAt: serverTimestamp(),
    });
    // The invite doc's id IS the code — matches firestore.rules, which
    // looks up inviteCodes/{request.resource.data.inviteCode} by that id.
    batch.set(doc(db, "inviteCodes", code), {
      facilityId, vendorId: vendorRef.id, used: false, createdAt: serverTimestamp(),
    });
    try {
      await batch.commit();
      setLastInvite({ code, vendorName: newCompanyName.trim() });
      setNewCompanyName(""); setAdding(false);
    } catch (e) {
      setErr("Couldn't add vendor — try again.");
    }
  };

  // Ends the open contract (rather than deleting it) and removes the
  // vendor doc — deleting the vendor doc is already safe per how
  // ReportPanel.jsx's resolveVendorName works: it reads the *contract's*
  // snapshotted vendorName, not a live join to vendors/{id}, so history
  // stays intact and correctly attributed even after this. Doesn't touch
  // any vendor_staff worker docs or the vendor's own login — a vendor
  // admin who's been removed just stops seeing anything for this
  // facilityId (their membership doc has no other tie to it).
  const removeVendor = async (vendor) => {
    const batch = writeBatch(db);
    const openContract = contractsFor(vendor.id).find(c => !c.endDate);
    if (openContract) batch.update(doc(db, "vendorContracts", openContract.id), { endDate: new Date().toISOString().slice(0, 10) });
    batch.delete(doc(db, "vendors", vendor.id));
    await batch.commit();
    setSelectedVendorId(null);
  };

  if (!facility) return <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted }}>Loading…</div>;

  return (
    <div style={S.page}>
      <div style={S.header} className="app-header">
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <img src={lockupLight} alt="Edifice View" style={{ height: 26, flexShrink: 0 }} />
          <div style={{ cursor: "pointer" }} onClick={() => setSwitcherOpen(o => !o)}>
            <div style={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
              {facility.name}
              <span style={{ fontSize: 11, color: "rgba(255,255,255,.55)" }}>▾ {myFacilities.length === 1 ? "1 building" : `${myFacilities.length} buildings`}</span>
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,.55)" }}>{facility.address}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={{ ...S.ghostBtn, width: "auto", padding: "8px 14px", color: C.mint, borderColor: C.mintDeep }}
            onClick={() => openBillingPortal(auth.currentUser.uid).then(({ url }) => { window.location.href = url; })}>
            Manage Billing
          </button>
          <button style={{ ...S.ghostBtn, width: "auto", padding: "8px 14px", color: C.mint, borderColor: C.mintDeep }} onClick={() => signOut(auth)}>Sign Out</button>
        </div>
      </div>

      {switcherOpen && (
        <div style={{ background: C.white, borderBottom: `1px solid ${C.border}`, padding: "16px 24px" }}>
          <div style={{ ...S.kicker, marginBottom: 10 }}>Your buildings</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
            {myFacilities.map(f => (
              <div key={f.id}
                style={{ padding: "10px 14px", borderRadius: 0, border: `1px solid ${f.id === facilityId ? C.mintDeep : C.border}`, background: f.id === facilityId ? C.tintAccent : C.white, minWidth: 160 }}>
                <div onClick={() => { setActiveFacilityId(f.id); setSwitcherOpen(false); }} style={{ cursor: "pointer" }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{f.name}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>{f.address}{f.isPrimary === false ? " · +$100/mo" : ""}</div>
                </div>
                {f.isPrimary === false && (
                  <div onClick={() => setRemovingBuildingId(f.id)} style={{ cursor: "pointer", fontSize: 11, color: C.red, marginTop: 6 }}>Remove</div>
                )}
              </div>
            ))}
          </div>

          {removingBuildingId && (() => {
            const target = myFacilities.find(f => f.id === removingBuildingId);
            if (!target) return null;
            return (
              <div style={{ border: `1px solid ${C.red}`, borderRadius: 0, padding: 16, marginBottom: 16, maxWidth: 420, background: C.tintDanger }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Remove "{target.name}"?</div>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
                  Its $100/mo charge ends right away. Nothing is deleted — vendors,
                  checklists, and reports for this building are kept — it just
                  won't appear here anymore.
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button style={S.btn(C.red)} disabled={removeBusy} onClick={() => removeBuilding(target)}>{removeBusy ? "Removing…" : "Remove Building"}</button>
                  <button style={{ ...S.ghostBtn, width: "auto", padding: "10px 16px" }} onClick={() => setRemovingBuildingId(null)}>Cancel</button>
                </div>
              </div>
            );
          })()}

          {!addingBuilding ? (
            <button style={{ ...S.ghostBtn, width: "auto", padding: "10px 16px", color: C.mintDeep, borderColor: C.mintDeep }} onClick={() => { setNewBuildingTimeZone(facility.timeZone || "America/Chicago"); setAddingBuilding(true); }}>+ Add Building</button>
          ) : (
            <div style={{ border: `1px solid ${C.border}`, borderRadius: 0, padding: 16, maxWidth: 420 }}>
              {buildingErr && <div style={S.err}>{buildingErr}</div>}
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
                Adding a building starts a new $100/mo charge on top of your base
                plan. It starts with a copy of {facility.name}'s vendors, internal
                staff, and assets/rooms (freely editable or removable afterward,
                each asset gets its own new QR code) so you're not redoing that
                setup from scratch — checklists and reports are fresh and fully
                separate.
              </div>
              <input style={S.input} placeholder="Building name (e.g. 900 Market St Annex)" value={newBuildingName} onChange={e => setNewBuildingName(e.target.value)} autoFocus />
              <input style={S.input} placeholder="Address" value={newBuildingAddress} onChange={e => setNewBuildingAddress(e.target.value)} />
              <select style={{ ...S.input, background: C.white }} value={newBuildingTimeZone} onChange={e => setNewBuildingTimeZone(e.target.value)}>
                {TIME_ZONES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              </select>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={S.btn()} onClick={addBuilding}>Add Building — $100/mo</button>
                <button style={{ ...S.ghostBtn, width: "auto", padding: "10px 16px" }} onClick={() => { setAddingBuilding(false); setBuildingErr(""); }}>Cancel</button>
              </div>
            </div>
          )}

          {removedFacilities.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div style={{ ...S.kicker, marginBottom: 10 }}>Removed buildings</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 420 }}>
                {removedFacilities.map(f => (
                  <div key={f.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderRadius: 0, border: `1px solid ${C.border}` }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{f.name}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{f.address}</div>
                    </div>
                    <button style={{ ...S.ghostBtn, width: "auto", padding: "6px 12px", color: C.mintDeep, borderColor: C.mintDeep }}
                      disabled={restoreBusy === f.id} onClick={() => restoreBuilding(f)}>
                      {restoreBusy === f.id ? "Restoring…" : "Restore — +$100/mo"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: 20, paddingTop: 20, borderTop: `1px solid ${C.border}` }}>
            <div style={{ ...S.kicker, marginBottom: 10 }}>Timezone — {facility.name}</div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 10, maxWidth: 420 }}>
              Used to tell when a scheduled visit counts as missed (see Alerts). Buildings created before this setting existed default to Central.
            </div>
            <select style={{ ...S.input, background: C.white, maxWidth: 280 }} value={facility.timeZone || "America/Chicago"}
              onChange={e => updateDoc(doc(db, "facilities", facilityId), { timeZone: e.target.value })}>
              {TIME_ZONES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
            </select>
          </div>

          <div style={{ marginTop: 20, paddingTop: 20, borderTop: `1px solid ${C.border}` }}>
            <div style={{ ...S.kicker, marginBottom: 10 }}>Staff access — {facility.name}</div>
            {staffAccessCode === undefined ? null : staffAccessCode === null ? (
              <div>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 10, maxWidth: 420 }}>
                  Generate a code for this building's staff — anyone with it can pick their name and enter their PIN from the home screen's "Staff" button, no QR code needed.
                </div>
                <button style={{ ...S.ghostBtn, width: "auto", padding: "10px 16px", color: C.mintDeep, borderColor: C.mintDeep }}
                  disabled={generatingCode} onClick={generateStaffAccessCode}>
                  {generatingCode ? "Generating…" : "Generate Staff Access Code"}
                </button>
              </div>
            ) : confirmingRevoke ? (
              <div style={{ border: `1px solid ${C.red}`, borderRadius: 0, padding: 16, maxWidth: 420, background: C.tintDanger }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Revoke this code?</div>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
                  {staffAccessCode} stops working immediately — use this if it's
                  been shared somewhere it shouldn't have been. Staff already
                  signed in stay signed in; only new logins with the old code
                  are blocked. A fresh code is generated right away.
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button style={S.btn(C.red)} disabled={generatingCode} onClick={revokeAndRegenerateCode}>{generatingCode ? "Revoking…" : "Revoke & Generate New Code"}</button>
                  <button style={{ ...S.ghostBtn, width: "auto", padding: "10px 16px" }} onClick={() => setConfirmingRevoke(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div style={{ fontFamily: "monospace", fontSize: 18, fontWeight: 800, letterSpacing: 1.5, background: C.white, padding: "6px 14px", borderRadius: 0, border: `1px solid ${C.border}` }}>{staffAccessCode}</div>
                <div style={{ fontSize: 12, color: C.muted }}>Share this with {facility.name}'s staff.</div>
                <button style={{ ...S.ghostBtn, width: "auto", padding: "6px 12px", fontSize: 12, color: C.red, borderColor: C.red }} onClick={() => setConfirmingRevoke(true)}>Revoke</button>
              </div>
            )}
          </div>
        </div>
      )}

      {copiedInvites && (
        <div style={{ background: C.tintAccent, borderBottom: `1.5px solid ${C.accent}`, padding: "16px 24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>New building started with {copiedInvites.length} carried-over vendor{copiedInvites.length === 1 ? "" : "s"}</div>
              <div style={{ fontSize: 13, color: C.muted, marginBottom: 8 }}>Each needs its own admin to join this building — send them these fresh, single-use codes (their existing login at your other building doesn't carry over automatically).</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {copiedInvites.map(inv => (
                  <div key={inv.code} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 13, minWidth: 160 }}>{inv.vendorName}</span>
                    <span style={{ fontFamily: "monospace", fontSize: 14, fontWeight: 800, letterSpacing: 1, background: C.white, padding: "4px 10px", borderRadius: 0, border: `1px solid ${C.border}` }}>{inv.code}</span>
                  </div>
                ))}
              </div>
            </div>
            <button style={{ ...S.ghostBtn, width: "auto", padding: "6px 12px" }} onClick={() => setCopiedInvites(null)}>Dismiss</button>
          </div>
        </div>
      )}

      <OnboardingChecklist facilityId={facilityId} vendorCount={vendors.length} onNavigate={setTab} />

      <ScrollHint className="app-tabbar" style={{ display: "flex", gap: 4, padding: "0 24px", background: C.white, borderBottom: `1px solid ${C.border}` }} fadeColor={C.white}>
        {[
          ["vendors", "Vendors"], ["assets", "Assets"], ["templates", "Checklist Templates"], ["staff", "Internal Staff"],
          ["workorders", "Work Orders"],
          ["scorecards", "Scorecards"], ["incidents", "Incidents"], ["reports", "Reports"], ["alerts", "Alerts"], ["messages", "Messages"],
          // Only worth showing once there's actually more than one building
          // to compare/report across — see PortfolioPanel.jsx.
          ...(myFacilities.length > 1 ? [["portfolio", "Portfolio"]] : []),
        ].map(([id, label]) => (
          <div key={id} onClick={() => setTab(id)}
            style={{ padding: "12px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600, color: tab === id ? C.mintDeep : C.muted, borderBottom: tab === id ? `2px solid ${C.mint}` : "2px solid transparent" }}>
            {label}
          </div>
        ))}
      </ScrollHint>

      <div style={S.body} className="app-body">
        {tab === "assets" && <AssetsPanel facilityId={facilityId} />}
        {tab === "templates" && <TemplatesPanel facilityId={facilityId} />}
        {tab === "staff" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, alignItems: "start" }} className="two-col-grid">
            <WorkersPanel facilityId={facilityId} vendorId={null} roleType="internal_staff" onSelect={setSelectedStaff} selectedId={selectedStaff?.id} />
            <div style={{ background: C.white, borderRadius: 0, border: `1px solid ${C.border}`, padding: 20, minHeight: 200 }}>
              {!selectedStaff ? (
                <div style={{ color: C.muted, fontSize: 13, textAlign: "center", paddingTop: 40 }}>Select a staff member to view their activity, schedule, and messages.</div>
              ) : (
                <EntityDetailPanel
                  facilityId={facilityId} scopeType="worker" scopeId={selectedStaff.id}
                  label={selectedStaff.name} sub={selectedStaff.trade}
                  viewerRole="facility_admin" viewerName={membership.name}
                />
              )}
            </div>
          </div>
        )}
        {tab === "workorders" && <FacilityWorkOrdersTab facilityId={facilityId} facilityName={facility.name} vendors={vendors} />}
        {tab === "scorecards" && <ScorecardPanel facilityId={facilityId} vendorId={null} vendors={vendors} />}
        {tab === "incidents" && <IncidentsPanel facilityId={facilityId} vendors={vendors} />}
        {tab === "portfolio" && myFacilities.length > 1 && <PortfolioPanel facilities={myFacilities} onNavigate={jumpFromPortfolio} />}
        {tab === "alerts" && <AlertsPanel facilityId={facilityId} facilityName={facility.name} />}
        {tab === "messages" && <MessagesPanel facilityId={facilityId} vendors={vendors} senderName={membership.name} />}
        {tab === "reports" && <ReportPanel facilityId={facilityId} vendorId={null} facilityName={facility.name} />}

        {tab === "vendors" && (<>
        {lastInvite && (
          <div style={{ background: C.tintAccent, border: `1.5px solid ${C.accent}`, borderRadius: 0, padding: 16, marginBottom: 20 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{lastInvite.vendorName} added</div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 8 }}>Send this invite code to their admin — it's single-use and scoped to this vendor only.</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontFamily: "monospace", fontSize: 20, fontWeight: 800, letterSpacing: 2, background: C.white, padding: "6px 14px", borderRadius: 0, border: `1px solid ${C.border}` }}>{lastInvite.code}</div>
              <button style={{ ...S.ghostBtn, width: "auto", padding: "6px 12px" }} onClick={() => setLastInvite(null)}>Dismiss</button>
            </div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
          <ScrollHint className="trade-scroll" outerStyle={{ flex: "1 1 auto", minWidth: 0 }} fadeColor={C.bg}>
            <button onClick={() => setTradeFilter("all")} style={{ ...S.pill(tradeFilter === "all" ? C.ink : C.white, tradeFilter === "all" ? C.white : C.muted), border: `1px solid ${C.border}`, cursor: "pointer", whiteSpace: "nowrap" }}>All trades</button>
            {TRADES.map(t => (
              <button key={t} onClick={() => setTradeFilter(t)} style={{ ...S.pill(tradeFilter === t ? C.ink : C.white, tradeFilter === t ? C.white : C.muted), border: `1px solid ${C.border}`, cursor: "pointer", whiteSpace: "nowrap" }}>{t}</button>
            ))}
          </ScrollHint>
          <button style={{ ...S.btn(), width: "auto", padding: "10px 18px", flexShrink: 0 }} onClick={() => setAdding(a => !a)}>{adding ? "Cancel" : "+ Add Vendor"}</button>
        </div>

        {adding && (
          <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 0, padding: 16, marginBottom: 20, maxWidth: 420 }}>
            {err && <div style={S.err}>{err}</div>}
            <input style={S.input} placeholder="Company name" value={newCompanyName} onChange={e => setNewCompanyName(e.target.value)} autoFocus />
            <select style={{ ...S.input, background: C.white }} value={newTrade} onChange={e => setNewTrade(e.target.value)}>
              {TRADES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <button style={S.btn(C.green)} onClick={addVendor}>Add &amp; Generate Invite Code</button>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, alignItems: "start" }} className="two-col-grid">
          <div style={{ background: C.white, borderRadius: 0, border: `1px solid ${C.border}`, overflow: "hidden" }}>
            {visibleVendors.length === 0 && (
              <div style={{ padding: 24, textAlign: "center", color: C.muted, fontSize: 13 }}>No vendors{tradeFilter !== "all" ? ` for ${tradeFilter}` : ""} yet.</div>
            )}
            {visibleVendors.map((v, idx) => (
              <div key={v.id} onClick={() => { setSelectedVendorId(v.id); setSelectedVendorWorker(null); }}
                style={{ padding: "14px 16px", cursor: "pointer", background: selectedVendorId === v.id ? C.tintAccent : "transparent", borderBottom: idx === visibleVendors.length - 1 ? "none" : `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{v.companyName}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>{v.trade}</div>
                </div>
                <span style={{ color: C.accent }}>→</span>
              </div>
            ))}
          </div>

          <div style={{ background: C.white, borderRadius: 0, border: `1px solid ${C.border}`, padding: 20, minHeight: 200 }}>
            {!selectedVendor ? (
              <div style={{ color: C.muted, fontSize: 13, textAlign: "center", paddingTop: 40 }}>Select a vendor to view its contract history and activity.</div>
            ) : selectedVendorWorker ? (<>
              <div onClick={() => setSelectedVendorWorker(null)} style={{ fontSize: 12, color: C.accent, cursor: "pointer", marginBottom: 16 }}>← Back to {selectedVendor.companyName}</div>
              <EntityDetailPanel
                facilityId={facilityId} scopeType="worker" scopeId={selectedVendorWorker.id} vendorId={selectedVendor.id}
                label={selectedVendorWorker.name} sub={selectedVendorWorker.trade}
                viewerRole="facility_admin" viewerName={membership.name}
              />
            </>) : (<>
              <EntityDetailPanel
                facilityId={facilityId} scopeType="vendor" scopeId={selectedVendor.id}
                label={selectedVendor.companyName} sub={selectedVendor.trade}
                viewerRole="facility_admin" viewerName={membership.name}
              >
                <div style={{ marginBottom: 20 }}>
                  <div style={{ ...S.kicker, marginBottom: 8 }}>Crew</div>
                  <WorkersPanel facilityId={facilityId} vendorId={selectedVendor.id} roleType="vendor_staff" onSelect={setSelectedVendorWorker} selectedId={selectedVendorWorker?.id} />
                </div>
              </EntityDetailPanel>

              <div style={{ marginTop: 20, paddingTop: 20, borderTop: `1px solid ${C.border}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <div style={{ ...S.kicker }}>Contract history</div>
                  <button style={{ ...S.ghostBtn, width: "auto", padding: "6px 12px", color: C.red, borderColor: C.red }}
                    onClick={() => removeVendor(selectedVendor)}>Remove Vendor</button>
                </div>
                {contractsFor(selectedVendor.id).map(c => (
                  <div key={c.id} style={{ fontSize: 13, padding: "6px 0", borderBottom: `1px solid ${C.border}` }}>
                    {c.startDate} → {c.endDate || "present"}
                  </div>
                ))}
              </div>
            </>)}
          </div>
        </div>
        </>)}
      </div>
    </div>
  );
}
